import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { CookieService, CookieOptions } from 'ngx-cookie';
import { from, Observable, of, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { decryptWithSeed, encryptWithSeed } from '@app/analytics/services/analytics-session-crypto';
import { OPTIMIZER_TOKEN_COOKIE } from '../interceptors/optimizer-auth.interceptor';

interface OptimizerLoginRequest {
  username: string;
  password: string;
}

/** Ответ /auth/login сервиса-оптимизатора: expires_in — В СЕКУНДАХ (не дата). */
interface OptimizerLoginResponse {
  access_token: string;
  token_type?: 'bearer';
  expires_in?: number;
}

/** В sessionStorage: password — base64(AES-GCM ciphertext), не plaintext. */
interface PersistedCreds {
  username: string;
  encPassword: string;
}

const SESSION_KEY = 'optimizer.creds.v1';
const MIN_REFRESH_DELAY_MS = 30_000;
const MAX_REFRESH_LEAD_MS = 5 * 60 * 1000;

/**
 * Тихий логин в сервис-оптимизатор — полное зеркало AnalyticsAuthService.
 * Отличие: сервис отдаёт expires_in (секунды), а не expires_at (дата) —
 * считаем срок сами. Креды берутся из логина ядра (auth.service вызывает
 * silentLogin теми же username/password), шифруются seed'ом = cookie 'token'
 * и лежат в sessionStorage для тихого ре-логина при протухании JWT.
 */
@Injectable({ providedIn: 'root' })
export class OptimizerAuthService implements OnDestroy {
  private _refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private _inflightRefresh: Promise<boolean> | null = null;

  constructor(
    private readonly _cookies: CookieService,
    private readonly _zone: NgZone,
  ) {
    if (!this.hasToken() && this._readPersisted()) {
      this._refreshFromPersisted();
    }
  }

  ngOnDestroy(): void {
    this._clearRefreshTimer();
  }

  hasToken(): boolean {
    return !!this._cookies.get(OPTIMIZER_TOKEN_COOKIE);
  }

  /** Тихий логин. Никогда не бросает — падение не ломает основное приложение. */
  silentLogin(form: OptimizerLoginRequest): Observable<OptimizerLoginResponse | null> {
    return from(this._doLogin(form)).pipe(
      tap((res) => {
        if (res?.access_token) {
          this._store(res);
          this._persist(form);
          this._scheduleRefresh(res.expires_in);
        }
      }),
      catchError(() => of(null)),
    );
  }

  /** Дёргается интерцептором при 401. Coalesce параллельных вызовов. */
  refreshFromPersisted(): Promise<boolean> {
    if (this._inflightRefresh) return this._inflightRefresh;
    this._inflightRefresh = this._refreshFromPersisted().finally(() => {
      this._inflightRefresh = null;
    });
    return this._inflightRefresh;
  }

  clearToken(): void {
    this._cookies.remove(OPTIMIZER_TOKEN_COOKIE);
    this._clearPersisted();
    this._clearRefreshTimer();
  }

  // ====== internals ======

  private async _doLogin(form: OptimizerLoginRequest): Promise<OptimizerLoginResponse | null> {
    // Нативный fetch — минуем Angular-интерсепторы (как в analytics).
    try {
      const res = await fetch(`${environment.optimizerApiUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) return null;
      return (await res.json()) as OptimizerLoginResponse;
    } catch {
      return null;
    }
  }

  private async _refreshFromPersisted(): Promise<boolean> {
    const persisted = this._readPersisted();
    if (!persisted) return false;
    const seed = this._cookies.get('token');
    if (!seed) return false;
    const password = await decryptWithSeed(persisted.encPassword, seed);
    if (!password) {
      this._clearPersisted(); // main-токен сменился → продлить нечем
      return false;
    }
    const res = await this._doLogin({ username: persisted.username, password });
    if (!res?.access_token) return false;
    this._store(res);
    this._scheduleRefresh(res.expires_in);
    return true;
  }

  private _store(res: OptimizerLoginResponse): void {
    const opts: CookieOptions = {};
    if (res.expires_in && Number.isFinite(res.expires_in)) {
      opts.expires = new Date(Date.now() + res.expires_in * 1000);
    }
    this._cookies.put(OPTIMIZER_TOKEN_COOKIE, res.access_token, opts);
  }

  private _persist(form: OptimizerLoginRequest): void {
    const seed = this._cookies.get('token');
    if (!seed) return;
    encryptWithSeed(form.password, seed)
      .then((encPassword) => {
        try {
          sessionStorage.setItem(
            SESSION_KEY,
            JSON.stringify({ username: form.username, encPassword } as PersistedCreds),
          );
        } catch {
          /* sessionStorage недоступен — терпим */
        }
      })
      .catch(() => {
        /* WebCrypto не сработал — пропускаем persist */
      });
  }

  private _readPersisted(): PersistedCreds | null {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as PersistedCreds;
      return parsed?.username && parsed?.encPassword ? parsed : null;
    } catch {
      return null;
    }
  }

  private _clearPersisted(): void {
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* ignore */
    }
  }

  private _scheduleRefresh(expiresIn?: number): void {
    this._clearRefreshTimer();
    if (!expiresIn || !Number.isFinite(expiresIn)) return;
    const remaining = expiresIn * 1000;
    const lead = Math.max(MIN_REFRESH_DELAY_MS, Math.min(remaining / 3, MAX_REFRESH_LEAD_MS));
    const delay = Math.max(MIN_REFRESH_DELAY_MS, remaining - lead);
    this._zone.runOutsideAngular(() => {
      this._refreshTimer = setTimeout(() => {
        this.refreshFromPersisted();
      }, delay);
    });
  }

  private _clearRefreshTimer(): void {
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer);
      this._refreshTimer = null;
    }
  }
}
